import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTMDBPopular, useTMDBTrending, useTMDBByGenre } from '../../../hooks/useTMDB';
import { CompactWatchlistButtons } from '../../../components/WatchlistButtons';
import { getPosterUrl } from '../../../config/tmdb';
import img4 from '../../../assets/images/movies/04.jpg';

const ViewAllTVShows = ({ type = 'popular', genreId = null }) => {
  const params = useParams();
  const urlType = params.type || type;
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('popularity.desc');

  // Call all hooks unconditionally
  const trendingResults = useTMDBTrending('week', 'tv', { page: currentPage });
  const popularResults = useTMDBPopular('tv', { sort_by: sortBy, page: currentPage });
  const genreResults = useTMDBByGenre(genreId, 'tv', { sort_by: sortBy, page: currentPage });

  // Select the appropriate data based on type
  const getSelectedData = () => {
    if (genreId) return genreResults;
    switch (urlType) {
      case 'trending': return trendingResults;
      default: return popularResults;
    }
  };

  const selectedData = getSelectedData();
  const { results, loading, error, totalPages, totalResults, setCurrentPage: setPage } = selectedData;

  const handlePageChange = (page) => {
    setPage(page);
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddClick = (Data) => {
    toast.success(Data, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar:false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const copyURL = () => {
    const url = window.location.href;
    const tempInput = document.createElement("input");
    document.body.appendChild(tempInput);
    tempInput.value = url;
    tempInput.select();
    document.execCommand("copy");
    document.body.removeChild(tempInput);

    toast.success("URL copied to clipboard: " + url);
  };

  const getTitle = () => {
    if (genreId) return 'TV Shows by Genre';
    switch (urlType) {
      case 'trending': return 'Trending TV Shows';
      case 'top-rated': return 'Top Rated TV Shows';
      default: return 'Popular TV Shows';
    }
  };

  const TVShowCard = ({ item, index }) => (
    <Col lg="3" md="4" sm="6" className="mb-4" key={`tv-${item.id}-${index}`}>
      <div className="block-images position-relative watchlist-first">
        <div className="img-box">
          <img
            src={item.imgSrc || getPosterUrl(item.poster_path) || img4}
            className="img-fluid"
            alt={item.title}
            style={{ width: '100%', height: '400px', objectFit: 'cover' }}
            onError={(e) => {
              e.target.src = img4;
            }}
          />
          {/* TV Show Label */}
          <div className="iq-tag position-absolute">
            <span className="bg-primary">TV</span>
          </div>
        </div>
        <div className="block-description">
          <h6 className="iq-title text-left">
            <Link to={`/tv-show-details/${item.id}`} className="text-decoration-none">
              {item.title}
            </Link>
          </h6>
          <div className="movie-time d-flex align-items-center my-2">
            <span className="text-white">{item.year || 'N/A'}</span>
            {item.vote_average && (
              <>
                <span className="text-white ml-2">•</span>
                <span className="text-white ml-2">
                  <i className="fa fa-star text-warning"></i> {Math.round(item.vote_average * 10) / 10}
                </span>
              </>
            )}
            {item.first_air_date && (
              <>
                <span className="text-white ml-2">•</span>
                <span className="text-white ml-2">TV Series</span>
              </>
            )}
          </div>
          <div className="hover-buttons text-left">
            <Link
              to={`/tv-show-details/${item.id}`}
              role="button"
              className="btn btn-hover"
            >
              <i className="fa fa-play mr-1" aria-hidden="true"></i>
              Watch Now
            </Link>
          </div>
        </div>
        <div className="block-social-info">
          <ul className="list-inline p-0 m-0 music-play-lists">
            <li className="share">
              <span>
                <i className="ri-share-fill"></i>
              </span>
              <div className="share-box">
                <div className="d-flex align-items-center">
                  <a
                    href="https://www.facebook.com/sharer?u=http://localhost:3000"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-ico"
                    tabIndex="0"
                  >
                    <i className="ri-facebook-fill"></i>
                  </a>
                  <a
                    href="https://twitter.com/intent/tweet?text=Check out this TV show!"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-ico"
                    tabIndex="0"
                  >
                    <i className="ri-twitter-fill"></i>
                  </a>
                  <a
                    onClick={copyURL}
                    className="share-ico iq-copy-link"
                    tabIndex="0"
                  >
                    <i className="ri-links-fill"></i>
                  </a>
                </div>
              </div>
            </li>
            <CompactWatchlistButtons item={{
              id: item.id,
              title: item.title,
              type: 'tv',
              poster_path: item.poster_path,
              imgSrc: item.imgSrc || getPosterUrl(item.poster_path),
              vote_average: item.vote_average,
              release_date: item.first_air_date || item.release_date,
              overview: item.overview,
              adult: item.adult
            }} />
          </ul>
        </div>
      </div>
    </Col>
  );

  // Pagination Component
  const Pagination = () => {
    const renderPaginationItems = () => {
      const items = [];
      const maxPagesToShow = 5;
      const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
      const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

      // Previous button
      if (currentPage > 1) {
        items.push(
          <button
            key="prev"
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            style={{
              padding: '8px 16px',
              margin: '0 4px',
              border: '1px solid #ddd',
              backgroundColor: 'transparent',
              color: '#fff',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Previous
          </button>
        );
      }

      // Page numbers
      for (let i = startPage; i <= endPage; i++) {
        items.push(
          <button
            key={i}
            className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
            onClick={() => handlePageChange(i)}
            style={{
              padding: '8px 16px',
              margin: '0 4px',
              border: '1px solid #ddd',
              backgroundColor: currentPage === i ? '#007bff' : 'transparent',
              color: currentPage === i ? '#fff' : '#fff',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {i}
          </button>
        );
      }

      // Next button
      if (currentPage < totalPages) {
        items.push(
          <button
            key="next"
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            style={{
              padding: '8px 16px',
              margin: '0 4px',
              border: '1px solid #ddd',
              backgroundColor: 'transparent',
              color: '#fff',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Next
          </button>
        );
      }

      return items;
    };

    if (totalPages <= 1) return null;

    return (
      <div className="d-flex justify-content-center mt-4">
        <div className="pagination-container">
          {renderPaginationItems()}
        </div>
      </div>
    );
  };

  return (
    <div>
      <Container fluid>
        <main id="main" className="site-main watchlist-contents">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title">{getTitle()}</h4>
            <div className="d-flex align-items-center">
              {!genreId && (
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="form-select mr-3"
                  style={{ 
                    backgroundColor: 'transparent', 
                    color: '#fff', 
                    border: '1px solid #555',
                    width: '200px'
                  }}
                >
                  <option value="popularity.desc">Most Popular</option>
                  <option value="vote_average.desc">Top Rated</option>
                  <option value="first_air_date.desc">Latest Release</option>
                  <option value="vote_count.desc">Most Votes</option>
                </select>
              )}
              <span className="text-muted">
                Showing {results.length} of {totalResults} TV shows
              </span>
            </div>
          </div>

          {/* Loading indicator */}
          {loading && (
            <div className="text-center py-4">
              <div className="spinner-border text-light" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="text-white mt-2">Loading TV shows...</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="text-center py-4">
              <div className="alert alert-danger" role="alert">
                <i className="ri-error-warning-line me-2"></i>
                Failed to load TV shows. Please try again.
              </div>
            </div>
          )}

          {/* TV Shows Grid */}
          {!loading && (
            <>
              <Row>
                {results.map((item, index) => (
                  <TVShowCard key={`tv-${item.id}-${index}`} item={item} index={index} />
                ))}
              </Row>
              
              {/* Pagination */}
              <Pagination />
            </>
          )}
        </main>
      </Container>
    </div>
  );
};

export default ViewAllTVShows;
